import { useQuery } from '@tanstack/react-query';
import { getLocations, getLocationBySlug, getLocationById, getFeaturedLocations } from '@/api/locations';
import { CustomerLocationDto } from '@/types';

// Freshness policy for prerender-seeded location data. `$locale.locations._index.tsx`,
// `$locale.catalogue._index.tsx` and `$locale.catalogue.$locationSlug.tsx` seed these
// exact query keys with build-time data via `queryClient.setQueryData` so first paint
// has zero network round-trips. Left at a long staleTime, that build-time snapshot
// would sit in cache untouched for the whole window (was 1h) — a location added or
// edited in the backoffice after the last deploy stayed invisible until a frontend
// redeploy. `staleTime: 0` makes the seeded snapshot immediately stale;
// `refetchOnMount: 'always'` forces a background refetch on every mount even though
// cached data already exists — the seeded snapshot still renders instantly, then this
// quietly refreshes it. Mirrors the LIVE_PRICE_OPTIONS pattern in app/hooks/useCatalog.ts.
const LIVE_LOCATION_OPTIONS = {
    staleTime: 0,
    refetchOnMount: 'always' as const,
};

/**
 * Hook to fetch all customer-facing locations
 */
export const useLocations = () => {
    return useQuery<CustomerLocationDto[], Error>({
        queryKey: ['locations'],
        queryFn: getLocations,
        ...LIVE_LOCATION_OPTIONS,
    });
};

/**
 * Hook to fetch featured locations
 */
export const useFeaturedLocations = () => {
    return useQuery<CustomerLocationDto[], Error>({
        queryKey: ['locations', 'featured'],
        queryFn: getFeaturedLocations,
        staleTime: 1000 * 60 * 60, // 1 hour — never seeded from a build-time loader, so no staleness bug to fix
    });
};

/**
 * Hook to fetch a single location by slug
 * @param slug - The URL-friendly slug of the location
 */
export const useLocationBySlug = (slug: string | undefined) => {
    return useQuery<CustomerLocationDto | null, Error>({
        queryKey: ['locations', 'by-slug', slug],
        queryFn: () => getLocationBySlug(slug!),
        enabled: !!slug,
        ...LIVE_LOCATION_OPTIONS,
    });
};

/**
 * Hook to fetch a single location by ID
 * @param id - The GUID of the location
 */
export const useLocationById = (id: string | undefined) => {
    return useQuery<CustomerLocationDto | null, Error>({
        queryKey: ['locations', id],
        queryFn: () => getLocationById(id!),
        enabled: !!id,
        staleTime: 1000 * 60 * 60, // 1 hour — never seeded from a build-time loader, so no staleness bug to fix
    });
};
